import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Package, ShoppingBag, DollarSign, Clock } from "lucide-react";
import api from "../../lib/api";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-[#A1A1AA] font-mono">loading...</div>;

  const stats = [
    { label: "Revenue", value: `$${data.total_revenue.toFixed(2)}`, icon: DollarSign },
    { label: "Total orders", value: data.total_orders, icon: ShoppingBag },
    { label: "Paid orders", value: data.paid_orders, icon: TrendingUp },
    { label: "Products", value: data.total_products, icon: Package },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66]">// dashboard</div>
      <h1 className="font-heading font-bold text-3xl mt-1 mb-8">Overview</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {stats.map((s) => (
          <div key={s.label} className="surface p-5">
            <s.icon className="w-4 h-4 text-[#00FF66]" />
            <div className="font-heading font-bold text-2xl mt-3">{s.value}</div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#71717A] mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Revenue chart */}
      {data.revenue_series && (
        <div data-testid="revenue-chart" className="surface p-6 mt-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading font-semibold text-lg">Revenue · last 14 days</h2>
            <div className="font-mono text-xs text-[#A1A1AA]">
              total <span className="text-[#00FF66] font-bold">${data.revenue_series.reduce((s, d) => s + d.revenue, 0).toFixed(2)}</span>
            </div>
          </div>
          <RevenueChart series={data.revenue_series} />
        </div>
      )}

      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-semibold text-lg flex items-center gap-2"><Clock className="w-4 h-4 text-[#00FF66]" />Recent orders</h2>
          <Link to="/admin/orders" className="font-mono text-xs text-[#A1A1AA] hover:text-[#00FF66]">view all →</Link>
        </div>
        <div className="surface overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1E293B] font-mono text-[11px] uppercase tracking-widest text-[#71717A]">
                <th className="text-left p-3">Order</th>
                <th className="text-left p-3">Email</th>
                <th className="text-right p-3">Total</th>
                <th className="text-left p-3">Payment</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_orders.length === 0 && (
                <tr><td colSpan="5" className="p-6 text-center text-[#A1A1AA] font-mono">no orders yet</td></tr>
              )}
              {data.recent_orders.map((o) => (
                <tr key={o.id} className="border-b border-[#1E293B] last:border-0 hover:bg-white/[0.02]">
                  <td className="p-3 font-mono text-[#00FF66]"><Link to={`/admin/orders/${o.id}`}>{o.id}</Link></td>
                  <td className="p-3">{o.email}</td>
                  <td className="p-3 text-right font-mono">${o.total.toFixed(2)}</td>
                  <td className="p-3"><span className="chip-mono">{o.payment_status}</span></td>
                  <td className="p-3"><span className="chip-mono">{o.order_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RevenueChart({ series }) {
  const max = Math.max(1, ...series.map((d) => d.revenue));
  return (
    <div className="flex items-end gap-1.5 h-40">
      {series.map((d, i) => {
        const h = max > 0 ? Math.max(2, (d.revenue / max) * 100) : 2;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1.5 group">
            <div className="text-[9px] font-mono text-[#71717A] opacity-0 group-hover:opacity-100 transition-opacity">
              ${d.revenue.toFixed(0)}
            </div>
            <div
              className="w-full rounded-t-sm transition-all"
              style={{
                height: `${h}%`,
                background: d.revenue > 0
                  ? "linear-gradient(180deg, #00FF66 0%, rgba(0,255,102,0.4) 100%)"
                  : "rgba(255,255,255,0.06)",
                boxShadow: d.revenue > 0 ? "0 0 12px rgba(0,255,102,0.25)" : "none",
              }}
              title={`${d.label}: $${d.revenue.toFixed(2)} · ${d.orders} orders`}
            />
            <div className="text-[9px] font-mono text-[#71717A] truncate w-full text-center">{d.label}</div>
          </div>
        );
      })}
    </div>
  );
}
